// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getUser, setUser as persistUser } from "../auth/auth";
import TopNav from "../components/TopNav";
import ListingFiltersBar from "../components/ListingFiltersBar";
import { getTrades } from "../api/trades";
import { resetBrowsingLocation, updateBrowsingLocation } from "../api/users";
import bountyPlaceholder from "../assets/bounty-placeholder.svg";
import LocationPickerModal from "../components/LocationPickerModal";
import useInfiniteScroll from "../hooks/useInfiniteScroll";
import {
  DEFAULT_TRADE_BROWSE_FILTERS,
  buildTradeBrowseParams,
  canClearTradeBrowseFilters,
  mergePagedItemsById,
} from "../utils/tradingBrowse";
import { formatLocationLabel, getBrowsingLocation, getBrowsingRadius } from "../utils/location";
import "../styles/LocalTrades.css";
import "../styles/ListingShared.css";
import {
  buildCardDetailUrl,
  buildCardId,
} from "../utils/marketAnalytics";

function LocalTrades() {
  const navigate = useNavigate();
  const [user, setUser] = useState(getUser());
  const [locationModalOpen, setLocationModalOpen] = useState(false);

  const [filters, setFilters] = useState(DEFAULT_TRADE_BROWSE_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState(DEFAULT_TRADE_BROWSE_FILTERS.q);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const requestKeyRef = useRef(0);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(filters.q);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [filters.q]);

  const { game, verifiedOnly, gradedOnly, zipCode } = filters;
  const browsingLocation = useMemo(() => getBrowsingLocation(user), [user]);
  const browsingRadiusMiles = useMemo(() => getBrowsingRadius(user), [user]);

  const appliedFilters = useMemo(
    () => ({
      q: debouncedSearch,
      zipCode,
      game,
      verifiedOnly,
      gradedOnly,
    }),
    [debouncedSearch, game, verifiedOnly, gradedOnly, zipCode],
  );

  const fetchTradesPage = useCallback(
    async (offset = 0, append = false) => {
      const requestKey = ++requestKeyRef.current;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setLoadingMore(false);
        setError("");
      }

      try {
        const params = buildTradeBrowseParams(filters, offset);
        delete params.zipCode;
        if (browsingLocation?.latitude != null && browsingLocation?.longitude != null) {
          params.lat = browsingLocation.latitude;
          params.lng = browsingLocation.longitude;
          params.radiusMiles = browsingRadiusMiles;
        }

        const response = await getTrades(params);
        if (requestKey !== requestKeyRef.current) return;

        const items = response?.items || [];
        setTrades((prev) => (append ? mergePagedItemsById(prev, items) : items));
        setHasMore(Boolean(response?.hasMore));
        setTotal(Number(response?.total || 0));
        setNextOffset(Number(response?.offset || offset) + items.length);
      } catch (fetchError) {
        if (requestKey !== requestKeyRef.current) return;
        console.error("Error fetching trades:", fetchError);
        setError(fetchError?.message || "Could not load trade listings.");
        if (!append) {
          setTrades([]);
          setHasMore(false);
          setNextOffset(0);
          setTotal(0);
        }
      } finally {
        if (requestKey === requestKeyRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [browsingLocation, browsingRadiusMiles, filters],
  );

  useEffect(() => {
    setTrades([]);
    setHasMore(false);
    setNextOffset(0);
    setTotal(0);
    void fetchTradesPage(0, false);
  }, [fetchTradesPage]);

  const handleLoadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    void fetchTradesPage(nextOffset, true);
  }, [fetchTradesPage, hasMore, loading, loadingMore, nextOffset]);

  const sentinelRef = useInfiniteScroll({
    enabled: hasMore,
    isLoading: loading || loadingMore,
    onLoadMore: handleLoadMore,
  });

  const canClear = canClearTradeBrowseFilters(filters);

  const handleBrowsingLocationSave = async (location, radiusMiles) => {
    if (!user?.id) return;
    const updatedUser = await updateBrowsingLocation(user.id, location, radiusMiles);
    setUser(updatedUser);
    persistUser(updatedUser);
  };

  const handleBrowsingReset = async () => {
    if (!user?.id) return;
    const updatedUser = await resetBrowsingLocation(user.id, browsingRadiusMiles);
    setUser(updatedUser);
    persistUser(updatedUser);
  };

  const TradeCard = ({ trade }) => {
    const cardId = trade.cardId || buildCardId(trade.cardNameCanonical || trade.cardName, trade.cardGame || trade.game);
    const cardLink = buildCardDetailUrl(cardId, trade.cardNameCanonical || trade.cardName, trade.cardGame || trade.game);
    const marketHint = trade.estimatedValue > 0
      ? `Listed around $${trade.estimatedValue}`
      : "View live market";
    const gradingCompany = String(trade.gradingCompany || "").toLowerCase();
    const gradeNumber = String(trade.gradeNumber || "");
    const isGraded = gradingCompany && gradingCompany !== "raw";
    const tradeCardClass = isGraded
      ? gradeNumber === "10"
        ? "shared-listing-card shared-listing-card--trade shared-listing-card--gem"
        : "shared-listing-card shared-listing-card--trade shared-listing-card--graded"
      : "shared-listing-card shared-listing-card--trade shared-listing-card--raw";

    return (
      <div className={tradeCardClass}>
        <div className="shared-listing-card-badge">Trade</div>
        <div className="trade-card-header">
          <div className="trade-card-title">
            <h3 className="shared-listing-card-title">{trade.cardName}</h3>
            <span className="game-badge">{trade.game}</span>
          </div>
          <div className="trade-card-meta">
            <span className="condition-badge">{trade.condition || "Condition N/A"}</span>
            {trade.estimatedValue > 0 && (
              <span className="value-badge">${trade.estimatedValue}</span>
            )}
          </div>
        </div>

        <div className="trade-card-body shared-listing-card-content">
          <img
            src={trade.photoUrl || bountyPlaceholder}
            alt={trade.cardName}
            className="trade-card-image"
            loading="eager"
            decoding="async"
            onError={(event) => {
              event.currentTarget.src = bountyPlaceholder;
            }}
          />
          <Link to={cardLink} className="market-hint">
            <span>{marketHint}</span>
            <span className="market-hint-sub">Open card insights</span>
          </Link>
          <div className="trade-detail">
            <span className="detail-label">Posted by:</span>
            <span className="detail-value">
              {trade.ownerUsername || "Trader"}
              {trade.ownerVerified && " ✓"}
              {trade.ownerTrustScore && ` (Trust: ${trade.ownerTrustScore})`}
            </span>
          </div>

          <div className="trade-detail">
            <span className="detail-label">Location:</span>
            <span className="detail-value">
              Postal code {trade.zipCode} ({trade.radius} mi radius)
            </span>
          </div>

          {trade.lookingFor && trade.lookingFor.length > 0 && (
            <div className="trade-detail">
              <span className="detail-label">Looking for:</span>
              <div className="looking-for-tags">
                {trade.lookingFor.map((item, idx) => (
                  <span key={idx} className="tag">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="trade-detail">
            <span className="detail-label">Meetup:</span>
            <span className="detail-value">
              {trade.meetupPreference === "public"
                ? "Public place only"
                : "Any location"}
            </span>
          </div>
        </div>

        <div className="trade-card-actions shared-listing-card-footer">
          {trade.userId !== user?.id ? (
            <button
              className="btn-make-offer"
              onClick={() => navigate(`/trade-offer/${trade.id}`)}
            >
              Make Offer
            </button>
          ) : (
            <span className="your-listing">Your Listing</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <TopNav />
      <div className="local-trades-page shared-listing-page-shell page-with-top-nav">
        <div className="local-trades-header">
          <div>
            <h1>Trade Listings</h1>
            <p>Browse cards posted for local, in-person trade</p>
            {user?.id ? (
              <p style={{ marginTop: 8 }}>
                {browsingLocation ? (
                  <>
                    Browsing in {formatLocationLabel(browsingLocation)}
                    {" · "}
                    <button type="button" className="link-button" onClick={() => setLocationModalOpen(true)}>
                      Change
                    </button>
                    {user?.profileLocation ? (
                      <>
                        {" · "}
                        <button type="button" className="link-button" onClick={() => void handleBrowsingReset()}>
                          Reset to profile
                        </button>
                      </>
                    ) : null}
                  </>
                ) : (
                  <>
                    Set your browsing area to see nearby trades
                    {" · "}
                    <button type="button" className="link-button" onClick={() => setLocationModalOpen(true)}>
                      Set area
                    </button>
                  </>
                )}
              </p>
            ) : null}
          </div>
        </div>

        <div className="action-buttons">
          <button
            className="btn-action btn-post-trade"
            onClick={() => navigate("/post-trade")}
          >
            Post a Trade
          </button>
        </div>

        <ListingFiltersBar
          mode="trade"
          searchValue={filters.q}
          onSearchChange={(q) => setFilters((prev) => ({ ...prev, q }))}
          zipValue={filters.zipCode}
          onZipChange={(zipCode) => setFilters((prev) => ({ ...prev, zipCode }))}
          hidePostalCode
          gameValue={filters.game}
          onGameChange={(game) => setFilters((prev) => ({ ...prev, game }))}
          verifiedOnly={filters.verifiedOnly}
          onVerifiedToggle={() => setFilters((prev) => ({ ...prev, verifiedOnly: !prev.verifiedOnly }))}
          gradedOnly={filters.gradedOnly}
          onGradedToggle={() => setFilters((prev) => ({ ...prev, gradedOnly: !prev.gradedOnly }))}
          onClear={() => setFilters(DEFAULT_TRADE_BROWSE_FILTERS)}
          canClear={canClear}
        />

        <div className="listing-results-meta">
          {!loading && !error && (
            <p>
              Showing {trades.length} of {total} trade listing{total === 1 ? "" : "s"}
            </p>
          )}
        </div>

        {error && <div className="shared-listing-error">{error}</div>}

        <div className="listings">
          {loading ? (
            <div className="shared-listing-loading">Loading trade listings...</div>
          ) : trades.length === 0 ? (
            <div className="empty-state shared-listing-empty">
              <p>No trade listings found</p>
              <p className="empty-subtitle">Try adjusting your filters or post a trade of your own.</p>
            </div>
          ) : (
            <>
              <div className="trades-grid shared-listing-grid">
                {trades.map((trade) => (
                  <TradeCard key={trade.id} trade={trade} />
                ))}
              </div>

              <div ref={sentinelRef} className="listing-scroll-sentinel" aria-hidden="true" />

              {loadingMore && <div className="shared-listing-loading">Loading more trades...</div>}
            </>
          )}
        </div>
      </div>
      <LocationPickerModal
        isOpen={locationModalOpen}
        title="Browsing Location"
        subtitle="This controls which trades appear nearby. It does not move your existing posts."
        confirmLabel="Save browsing area"
        initialLocation={browsingLocation}
        initialRadiusMiles={browsingRadiusMiles}
        showRadius
        onClose={() => setLocationModalOpen(false)}
        onConfirm={handleBrowsingLocationSave}
      />
    </>
  );
}

export default LocalTrades;
